import React from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons/faCloudArrowUp';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Button } from 'src/components/design-system';
import { FILE_ERRORS } from '../use-shifts-upload.hook';
import type { FileError } from '../use-shifts-upload.hook';
import type { ImportResult, LastUpload } from '../helpers/upload.types';
import { FilePicker } from './file-picker';
import { ImportResultCard } from './import-result-card';
import styles from './choose-file-step.module.scss';

const FILE_ERROR_LANG_KEYS: Record<FileError, string> = {
  [FILE_ERRORS.UNSUPPORTED_TYPE]: 'SHIFTS_INVALID_FILE',
  [FILE_ERRORS.UNREADABLE]: 'SHIFTS_UNREADABLE_FILE',
};

type ChooseFileStepProps = {
  fileError: FileError | null;
  isReadingFile: boolean;
  lastUpload: LastUpload | null;
  results: ImportResult[];
  onChooseFile: (file: File) => void;
  onDismissResult: (id: string) => void;
};

export const ChooseFileStep = ({
  fileError,
  isReadingFile,
  lastUpload,
  results,
  onChooseFile,
  onDismissResult,
}: ChooseFileStepProps) => {
  const lang = useSelector(getLang('SETTINGS'));

  return (
    <div>
      {lastUpload && (
        <span className={styles.lastUpload}>
          <span className={styles.lastUploadLabel}>{lang.SHIFTS_LAST_UPLOAD}</span>{' '}
          {new Date(lastUpload.uploadedAt).toLocaleString()}
        </span>
      )}

      <div className={styles.banner}>
        <FontAwesomeIcon icon={faCircleInfo} className={styles.bannerIcon} />
        <span>{lang.SHIFTS_OVERWRITE_NOTICE}</span>
      </div>

      <FilePicker
        asDropzone
        onPick={onChooseFile}
        disabled={isReadingFile}
        rootClassName={styles.dropzone}>
        <FontAwesomeIcon
          icon={isReadingFile ? faSpinner : faCloudArrowUp}
          spin={isReadingFile}
          className={styles.dropzoneIcon}
        />
        <span className={styles.dropzoneTitle}>
          {lang.SHIFTS_DROP_FILE} <span className={styles.browse}>{lang.SHIFTS_BROWSE}</span>
        </span>
        <span className={styles.dropzoneHint}>{lang.SHIFTS_ACCEPTED_TYPES}</span>
      </FilePicker>

      {fileError && (
        <div className={styles.errorCard}>
          <FontAwesomeIcon icon={faCircleExclamation} className={styles.errorIcon} />
          <div className={styles.errorBody}>
            <span className={styles.errorTitle}>{lang.SHIFTS_INVALID_FILE_TITLE}</span>
            <span className={styles.errorText}>{lang[FILE_ERROR_LANG_KEYS[fileError]]}</span>
          </div>
          <FilePicker onPick={onChooseFile}>
            <Button type="default">{lang.SHIFTS_CHOOSE_ANOTHER_FILE}</Button>
          </FilePicker>
        </div>
      )}

      {results.length > 0 && (
        <div className={styles.results}>
          {results.map(result => (
            <ImportResultCard key={result.id} result={result} onDismiss={onDismissResult} />
          ))}
        </div>
      )}
    </div>
  );
};
